import type { FC, ReactNode } from 'react'
import type { GetStaticPaths, GetStaticProps } from 'next'
import type { BlogPost, BlogSummary } from '@/types'

import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Insights } from '@/components/sections/insights'
import { Cta } from '@/components/sections/cta'
import { formatDate } from '@/lib/format'

import {
    getBlogPost,
    getBlogSlugs,
    getRelatedPosts
} from '@/lib/content'

type BlogDetailProps = {
    post: BlogPost
    related: BlogSummary[]
}

const BlogDetail: FC<BlogDetailProps> = ({ post, related }): ReactNode => {
    return (
        <Layout>
            <Seo title={post.title} description={post.excerpt} ogImage={post.coverImage} />

            <section className='hero-section section-padding'>
                <div className='w-layout-blockcontainer hero-container blog-details w-container'>
                    <div className='hero-title-wrapper align-center'>
                        <div className='blog-category-wrapper' data-hero-reveal>
                            <p className='blog-category'>
                                {formatDate(post.publishedOn)}
                            </p>
                        </div>

                        <h1
                            className='hero-title'
                            data-hero-reveal
                            data-mdlfy-cms={`blog_posts:${post.id}:title`}
                        >
                            {post.title}
                        </h1>

                        <p
                            className='hero-subtitle blog'
                            data-hero-reveal
                            data-mdlfy-cms={`blog_posts:${post.id}:excerpt`}
                        >
                            {post.excerpt}
                        </p>
                    </div>
                </div>
            </section>

            <section className='blog-section-02 padding-bottom'>
                <div className='w-layout-blockcontainer hero-container w-container'>
                    <div className='section-content-wrapper blog-details'>
                        <div className='single-blog-thumbnail-wrapper'>
                            <img
                                src={post.coverImage}
                                loading='lazy'
                                alt={post.coverImageAlt}
                                className='single-blog-thumbnail'
                                data-reveal='image'
                            />
                        </div>

                        <div
                            className='single-blog-content w-richtext'
                            data-reveal
                            dangerouslySetInnerHTML={{ __html: post.body }}
                        />
                    </div>
                </div>
            </section>

            <Insights
                posts={related}
                heading='Keep reading'
                subheading='More painting tips, seasonal advice and practical guides from our team.'
            />

            <Cta />
        </Layout>
    )
}

export const getStaticPaths: GetStaticPaths = async () => {
    const slugs = await getBlogSlugs()

    return {
        paths: slugs.map((slug) => ({ params: { slug } })),
        fallback: 'blocking'
    }
}

export const getStaticProps: GetStaticProps<BlogDetailProps> = async ({ params }) => {
    const slug = typeof params?.slug === 'string' ? params.slug : ''
    const post = await getBlogPost(slug)

    if (!post) {
        return { notFound: true, revalidate: 60 }
    }

    const related = await getRelatedPosts(slug, 4)

    return {
        props: { post, related },
        revalidate: 60
    }
}

export default BlogDetail
